import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  HiArrowPath, HiChatBubbleLeftRight, HiStar, HiOutlineStar, HiUser,
} from 'react-icons/hi2'
import { feedbackApi } from '../api'
import { Loader } from '../ui'

// Yulduz filtri — yorliq matn, yulduz belgisi ikonka bilan chiziladi (DESIGN.md 8)
const STAR_FILTERS = [
  [0, 'Barchasi'],
  [5, '5'],
  [4, '4+'],
  [3, '3+'],
]

function Stars({ score }) {
  const n = score || 0
  return (
    <span
      role="img"
      aria-label={`Baho: 5 dan ${n}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 2, whiteSpace: 'nowrap' }}
    >
      {[1, 2, 3, 4, 5].map(i => {
        const Icon = i <= n ? HiStar : HiOutlineStar
        return (
          <Icon key={i} size={14} aria-hidden="true"
            style={{ color: i <= n ? 'var(--text-1)' : 'var(--border-2)' }} />
        )
      })}
    </span>
  )
}

function fmtDate(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('uz-UZ') + ' ' + d.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })
  } catch { return '' }
}

export default function Feedback() {
  const navigate = useNavigate()
  const [items, setItems]   = useState([])
  const [total, setTotal]   = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState(null)
  const [minScore, setMinScore] = useState(0)
  const [onlyComments, setOnlyComments] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await feedbackApi.list({ min_score: minScore, only_comments: onlyComments, limit: 200 })
      setItems(res.items || [])
      setTotal(res.total || 0)
    } catch (e) {
      setError(e.message || 'Xatolik')
    }
    setLoading(false)
  }, [minScore, onlyComments])

  useEffect(() => { load() }, [load])

  // Filtr tugmasi: faol holat — birlamchi tugma (fon --text-1, matn --surface)
  const chip = (active) => ({
    padding: '6px 12px', borderRadius: 'var(--r)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--text-1)' : 'var(--border)'}`,
    background: active ? 'var(--text-1)' : 'var(--surface)',
    color: active ? 'var(--surface)' : 'var(--text-2)',
    transition: 'background var(--dur-1) var(--ease), border-color var(--dur-1) var(--ease)',
  })

  return (
    <div className="page-enter">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <h2>Fikrlar</h2>
          <p>Fuqarolar qoldirgan baholar va fikr-mulohazalar</p>
        </div>
        <div className="page-hdr-right">
          <button className="btn-refresh-icon" onClick={load} title="Yangilash" aria-label="Yangilash">
            <HiArrowPath aria-hidden="true" />
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'var(--ui-st-critical-bg)', border: '1px solid var(--ui-st-critical)',
          color: 'var(--ui-st-critical)', padding: 12, borderRadius: 'var(--r)', marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {STAR_FILTERS.map(([k, lbl]) => (
          <button key={k} onClick={() => setMinScore(k)}
            style={{ ...chip(minScore === k), display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {lbl}
            {k > 0 && <HiStar size={12} aria-hidden="true" />}
          </button>
        ))}
        <button onClick={() => setOnlyComments(v => !v)} aria-pressed={onlyComments}
          style={{ ...chip(onlyComments), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <HiChatBubbleLeftRight size={14} aria-hidden="true" /> Faqat matnli
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)' }}>Jami: {total}</span>
      </div>

      {loading ? (
        // Oddiy matn o'rniga kitning belgisi: ro'yxat saralanmoqda — "searching"
        <Loader state="searching" size="lg" block />
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-3)' }}>
          <HiStar size={40} aria-hidden="true" style={{ opacity: 0.4, marginBottom: 8 }} />
          <div>Hozircha fikrlar yo'q</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(row => (
            <div key={row.id}
              onClick={() => navigate(`/ticket/${row.id}`)}
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
                padding: '14px 16px', cursor: 'pointer', transition: 'border-color var(--dur-1) var(--ease)' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-2)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Stars score={row.score} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>{row.citizen_name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>{row.ticket_number}</span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{fmtDate(row.resolved_at)}</span>
              </div>

              {row.comment ? (
                <div style={{ fontSize: 13, color: 'var(--text-1)', marginTop: 8, lineHeight: 1.5,
                  display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <HiChatBubbleLeftRight size={14} aria-hidden="true"
                    style={{ flexShrink: 0, marginTop: 3, color: 'var(--text-3)' }} />
                  <span>{row.comment}</span>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6, fontStyle: 'italic' }}>
                  Matnsiz baho
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11, color: 'var(--text-3)', flexWrap: 'wrap' }}>
                {row.operator_name && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <HiUser size={12} aria-hidden="true" /> {row.operator_name}
                  </span>
                )}
                {row.subject && <span style={{ opacity: 0.8 }}>· {row.subject}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
